import { createContext, useContext, useEffect, useState } from "react";
import { api, apiErrToStr } from "@/lib/api";

const AuthCtx = createContext(null);
const ACTIVE_TENANT_STORAGE_KEY = "voxyra-active-tenant-id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = anon, obj = logged
  const [error, setError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantIdState] = useState(() => localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY) || "");

  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";

  const setActiveTenantId = (value) => {
    const next = String(value || "").trim();
    setActiveTenantIdState(next);
    if (next) localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, next);
    else localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  };

  const loadSuperadminTenants = async () => {
    if (!isSuperadmin) {
      setTenants([]);
      setActiveTenantId("");
      return;
    }
    try {
      const { data } = await api.get("/tenants", { headers: { "x-active-tenant-id": "" } });
      const rows = data || [];
      setTenants(rows);
      setActiveTenantIdState((current) => {
        const selected = String(current || localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY) || "").trim();
        if (selected && rows.some((row) => row.id === selected)) return selected;
        const fallback = String(rows[0]?.id || "").trim();
        if (fallback) localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, fallback);
        return fallback;
      });
    } catch {
      setTenants([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    loadSuperadminTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      if (String(data?.role || "").toLowerCase() !== "superadmin") {
        setActiveTenantId("");
      }
      return true;
    } catch (e) {
      setError(apiErrToStr(e?.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginAgent = async (username, password, extension) => {
    setError("");
    try {
      const { data } = await api.post("/auth/agent-login", { username, password, extension });
      setUser(data);
      setActiveTenantId("");
      return true;
    } catch (e) {
      setError(apiErrToStr(e?.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginExtension = async (username, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/extension-login", { username, password });
      setUser(data);
      setActiveTenantId("");
      return true;
    } catch (e) {
      setError(apiErrToStr(e?.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_err) { /* ignore */ }
    setTenants([]);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, login, loginAgent, loginExtension, logout, error, tenants, activeTenantId, setActiveTenantId, reloadTenants: loadSuperadminTenants }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
